import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

import { CandidateSessionService } from './candidate-session.service';
import { LiteWelcomeService } from '../../shared/lite-login/lite-welcome.service';
import { AccountService } from './account.service';

/**
 * Blocks temporary (candidate) users from the knowledge base — the org-structure node/folder tree
 * (`/node`, `/node-fullscreen`) and the QR `jumpto` resolver that forwards into it.
 *
 * <p>Client decision: temporary users may only use their "my *" areas (Home, My Work, My E-Learning,
 * My Surveys, …) and must not browse the knowledge base. Because the `/node` route is not feature-gated
 * and `SYSTEM_U0` (the temp-user role) is shared with regular employees, the cleanest lever is this
 * single choke point keyed off the candidate session — every entry point into the tree (topbar search,
 * QR jumpto, breadcrumb, direct URL) routes through here, so one guard covers them all.</p>
 *
 * <p>Candidate detection uses {@link CandidateSessionService} (the `candidate:true` JWT claim), the
 * app-wide temp-user signal. Frontend-only, matching the app's frontend-gating convention; candidates
 * are simply redirected Home rather than shown a denial page.</p>
 */
export const blockCandidateFromKnowledgeBaseGuard: CanActivateFn = (): boolean | UrlTree => {
  const candidateSession = inject(CandidateSessionService);
  const router = inject(Router);

  // LB-1893: Leanbyte Lite users (temporary users on the Lite instance) work in their own folder.
  if (candidateSession.isActive() && !inject(LiteWelcomeService).isLiteSession(null)) {
    return router.createUrlTree(['/']);
  }
  return true;
};

/**
 * LB-1893: a Leanbyte Lite user+ only views My Work and My E-Learning; running a task or training (the
 * `/checklist` execution page, which saves progress as soon as it opens) is not allowed, also not via a direct URL.
 */
export const blockLitePlusFromExecutionGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const liteWelcome = inject(LiteWelcomeService);
  const router = inject(Router);
  await inject(AccountService).identity(); // the role is only known once the account is loaded (direct URL)
  return liteWelcome.isLitePlus() ? router.createUrlTree(['/']) : true;
};
